import type { Customer, DocumentLanguage, InvoiceCurrency, LineItem, PaymentMethod, Seller, Totals } from "@/services/invoiceGeneration";
import {
  buildInterestSummaryLine,
  buildPreviewContent,
  formatMoneyForCurrency,
  t,
} from "@/services/invoiceGeneration";

import { useTranslations } from "use-intl";
import { BRAND_NAME } from "@financialharmony/ui/brand";

interface Props {
  seller: Seller | null;
  customer: Customer | null;
  lines: LineItem[];
  totals: Totals;
  invoiceDate: string;
  dueDate: string;
  ourReference: string;
  theirReference: string;
  number: string | null;
  ocr: string | null;
  vatWording?: string | null;
  issued?: boolean;
  /** `null` renders no dröjsmålsränta line; `0` renders an explicit "0 %". */
  interestRatePercent?: number | null;
  /** The sender's own message. Empty renders nothing at all. */
  freeText?: string;
  /**
   * How this invoice is paid. Bankgiro renders the giro block in place of the account
   * lines and suppresses the explanation; bank account does the reverse.
   */
  paymentMethod?: PaymentMethod;
  /** FIN-1580: the invoice document's own language/currency — independent of this app's own UI language. */
  documentLanguage: DocumentLanguage;
  currency: InvoiceCurrency;
}

export function InvoicePreview({
  seller, customer, lines, totals, invoiceDate, dueDate,
  ourReference, theirReference, number, ocr, vatWording, issued,
  interestRatePercent, freeText, paymentMethod, documentLanguage, currency,
}: Props) {
  const lang = documentLanguage;
  const money = (n: number) => formatMoneyForCurrency(n, currency);
  // The preview's shared content model (US1.AC3) — the same statutory-note gate and payment
  // box the issued PDF renderer prints, so the preview never disagrees with the PDF (EC10).
  const {
    visibleLines, freeText: trimmedFreeText, statutoryNote, paymentBox,
  } = buildPreviewContent({ seller, lines, paymentMethod, freeText, interestRatePercent, documentLanguage });
  const tp = useTranslations("consultantInvoiceCreate.preview");
  return (
    <div className="relative overflow-hidden rounded-lg border bg-card p-6 shadow-sm md:p-8">
      {!issued && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="rotate-[-24deg] text-7xl font-black tracking-widest text-muted-foreground/10">
            {tp("draftWatermark")}
          </span>
        </div>
      )}

      <div className="relative flex items-start justify-between">
        <div>
          {seller?.logoUrl ? (
            <img src={seller.logoUrl} alt={tp("logoAlt")} className="mb-2 h-10 w-auto max-w-[160px] object-contain" />
          ) : (
            <div className="mb-2 flex h-9 w-9 items-center justify-center rounded bg-primary text-xs font-bold text-primary-foreground">
              {seller?.name?.[0] ?? BRAND_NAME[0]}
            </div>
          )}
          <p className="font-semibold">{seller?.name ?? "—"}</p>
          <p className="text-xs text-muted-foreground">{t("seller.orgNumber", lang)} {seller?.orgNumber ?? "—"}</p>
          <p className="text-xs text-muted-foreground">{t("seller.vatNumber", lang)} {seller?.vatNumber ?? "—"}</p>
          <p className="text-xs text-muted-foreground">{seller?.address ?? "—"}</p>
          {seller?.fSkatt && <p className="text-xs text-muted-foreground">{t("seller.fSkatt", lang)}</p>}
        </div>
        <div className="text-right">
          <p className="text-lg font-bold">{t("heading.title", lang)}</p>
          <dl className="mt-2 space-y-0.5 text-xs">
            <Row k={t("heading.invoiceNumber", lang)} v={number ?? "—"} />
            <Row k={t("heading.invoiceDate", lang)} v={invoiceDate} />
            <Row k={t("heading.dueDate", lang)} v={dueDate === invoiceDate ? `${dueDate}${t("heading.dueImmediately", lang)}` : dueDate} />
            {interestRatePercent != null && (
              <div className="pt-1 text-left text-muted-foreground">{buildInterestSummaryLine(interestRatePercent, undefined, lang)}</div>
            )}
          </dl>
        </div>
      </div>

      <div className="relative mt-6 flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase text-muted-foreground">{t("customer.heading", lang)}</p>
          {customer ? (
            <>
              <p className="text-sm font-medium">{customer.name}</p>
              <p className="text-xs text-muted-foreground">{customer.address.street}</p>
              <p className="text-xs text-muted-foreground">
                {customer.address.postalCode} {customer.address.city}
              </p>
              {customer.vatNumber && <p className="text-xs text-muted-foreground">{t("customer.vatNumber", lang)} {customer.vatNumber}</p>}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{t("customer.none", lang)}</p>
          )}
        </div>
        <div className="text-right text-xs text-muted-foreground">
          <p>{t("customer.ourReference", lang)} {ourReference || "—"}</p>
          <p>{t("customer.theirReference", lang)} {theirReference || "—"}</p>
        </div>
      </div>

      <table className="relative mt-6 w-full text-sm">
        <thead>
          <tr className="border-b text-xs uppercase text-muted-foreground">
            <th className="py-2 text-left font-medium">{t("table.description", lang)}</th>
            <th className="py-2 text-right font-medium">{t("table.qty", lang)}</th>
            <th className="py-2 text-right font-medium">{t("table.unitPrice", lang)}</th>
            <th className="py-2 text-right font-medium">{t("table.vat", lang)}</th>
            <th className="py-2 text-right font-medium">{t("table.amount", lang)}</th>
          </tr>
        </thead>
        <tbody>
          {visibleLines.length === 0 ? (
            <tr>
              <td colSpan={5} className="py-6 text-center text-sm text-muted-foreground">
                {t("table.empty", lang)}
              </td>
            </tr>
          ) : (
            visibleLines.map((l) => (
              <tr key={l.id} className="border-b border-border/60">
                <td className="py-2">{l.description || "—"}</td>
                <td className="py-2 text-right">{l.quantity} {l.unit}</td>
                <td className="py-2 text-right">{money(l.unitPrice)}</td>
                <td className="py-2 text-right">{l.vatRate}%</td>
                <td className="py-2 text-right">{money(l.quantity * l.unitPrice)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="relative mt-4 ml-auto w-full max-w-xs space-y-1 text-sm">
        <Row k={t("totals.net", lang)} v={money(totals.net)} />
        {totals.vatGroups.map((g) => (
          <Row key={g.rate} k={`${t("table.vat", lang)} ${g.rate}%`} v={money(g.vat)} />
        ))}
        {totals.rounding !== 0 && <Row k={t("totals.rounding", lang)} v={money(totals.rounding)} />}
        <div className="flex justify-between border-t pt-2 text-base font-bold">
          <span>{t("totals.toPay", lang)}</span>
          <span>{money(totals.toPay)}</span>
        </div>
      </div>

      {vatWording && (
        <p className="relative mt-3 text-xs font-medium text-muted-foreground">{vatWording}</p>
      )}

      {trimmedFreeText && (
        <p className="relative mt-3 whitespace-pre-line text-sm text-foreground">{trimmedFreeText}</p>
      )}

      {statutoryNote && (
        <p className="relative mt-6 text-[11px] italic text-muted-foreground">{statutoryNote}</p>
      )}

      <section className="relative mt-2 rounded-md border-[1.5px] border-foreground p-3 text-xs" data-testid="payment-box">
        <h4 className="text-[10px] font-bold uppercase tracking-wide">{paymentBox.heading}</h4>
        {[
          ...paymentBox.accountRows,
          { label: paymentBox.referenceLabel, value: ocr ?? t("payment.noOcrYet", lang) },
          { label: t("payment.amountDueLabel", lang), value: money(totals.toPay) },
        ].map(({ label, value }) => <p key={label} className="mt-1">{label}: <strong>{value}</strong></p>)}
        {paymentBox.foreignRows.length > 0 && <h5 className="mt-2 text-[10px] uppercase text-muted-foreground">{t("payment.fromAbroad", lang)}</h5>}
        {paymentBox.foreignRows.map(({ label, value }) => <p key={label} className="mt-1">{label}: <strong>{value}</strong></p>)}
      </section>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-6">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium text-foreground">{v}</span>
    </div>
  );
}
